import {
    Force_APIListing,
    Force_APITestDrive,
    Force_Extension,
    Force_PackageListing,
    Force_PackageTestDrive,
    schemas,
} from '@oneappexchange/oaf-schema';
import { validate as isUUID } from 'uuid';
import { TryOptionType, TryOptionTypes } from '~/modules/appx_site_schema';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '../services/listing_service';

const EXTENSION_TYPE_PACKAGE_LISTING = 'listing/extensions/force/listings/Listing/extensions/PackageListing';
const EXTENSION_TYPE_API_LISTING = 'listing/extensions/force/listings/Listing/extensions/APIListing';
const EXTENSION_TYPE_TEST_DRIVE = 'listing/extensions/force/listings/Listing/extensions/PackageListing/TestDrive';
const EXTENSION_TYPE_API = 'listing/extensions/force/listings/Listing/extensions/APIListing/TestDrive';

// The listingId that we store in the userAccount comes directly from the url.
// As a result we dont know which id it corresponds to, so we must check all the Ids.
export function isListingMatchingId(listing: ListingResponse, listingId: string) {
    return (
        listing.appExchangeId === listingId ||
        listing.oafId === listingId ||
        listing.tzId === listingId ||
        listing.external_id === listingId
    );
}

export function getListingId(listing: ListingResponse) {
    return listing?.appExchangeId || listing?.oafId || listing?.tzId || listing?.external_id || '';
}

export const TECHNOLOGY_EXTENSION_MAP: Record<
    schema.oaf.BaseListing['technology'],
    schema.oaf.ListingExtension['extensionType']
> = {
    'force/bolt': 'listing/extensions/force/listings/Listing',
    'force/copilot': 'listing/extensions/force/listings/CopilotListing',
    'force/flow': 'listing/extensions/force/listings/Listing',
    'force/lightning': 'listing/extensions/force/listings/Listing',
    'force/package': 'listing/extensions/force/listings/Listing',
    'salesforce-platform/api': 'listing/extensions/force/listings/Listing',
    'quip/package': 'listing/extensions/quip/listings/Listing',
    'b2c-commerce/package': 'listing/extensions/b2c/listings/Listing',
    'b2c-commerce/api': 'listing/extensions/b2c/listings/Listing',
    'marketing-cloud/api': 'listing/extensions/marketing-cloud/listings/Listing',
    'tableau/accelerator': 'listing/extensions/tableau/listings/Listing',
    consultant: 'listing/extensions/consultant/listings/Listing',
};

export function isSupportedTechnologyType(listing?: ListingResponse) {
    const extensionType = listing && TECHNOLOGY_EXTENSION_MAP[listing.technology];
    if (!extensionType || extensionType === 'listing/extensions/consultant/listings/Listing') {
        return false;
    }
    return true;
}

export function selectExtensionListing<T = schema.oaf.Force_Listing>(listing: ListingResponse) {
    if (!listing?.technology) {
        return schemas['listing/extensions/force/listings/Listing'].new() as unknown as T;
    }

    const extensionType = TECHNOLOGY_EXTENSION_MAP[listing.technology];
    if (!extensionType) {
        console.error(
            `Failed to find extensionType for listing with technology '${listing.technology}' we might be missing an entry in TECHNOLOGY_EXTENSION_MAP`,
        );
        return schemas['listing/extensions/force/listings/Listing'].new() as unknown as T;
    }

    const extension = listing.extensions?.find((test) => test.extensionType === extensionType)?.data as unknown as T;
    if (!extension) {
        console.error(
            `Failed to find listing extension '${extensionType}' for listing: external_id=${listing.external_id}, technology=${listing.technology}`,
        );
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return schemas[extensionType].new() as unknown as T;
    }
    return extension;
}

export function isValidListingId(listingId?: string): listingId is string {
    // Deny falsy values (null, undefined, empty strings)
    if (!listingId) {
        return false;
    }

    const numChars = listingId.length;

    // Allow UUIDs
    if (numChars === 36) {
        return isUUID(listingId);
    }

    // Allow SFIDs
    return numChars === 18 || numChars === 15;
}

export function isListingReviewed(listing: ListingResponse | null): boolean {
    if (!listing) {
        return false;
    }

    const approvalDate = listing.solution?.securityReview?.approvalDate;
    return !!approvalDate;
}

export function generateListingUrl(baseUrl: string, listing?: ListingResponse | null, params?: string) {
    if (listing?.technology === 'consultant') {
        return `${baseUrl}/${Routes.CONSULTANT_LISTING}${params ? `?${params}` : ''}`;
    }
    return `${baseUrl}/${Routes.LISTING}${params ? `?${params}` : ''}`;
}

export function selectListingTryOptions(listing: ListingResponse): Array<TryOptionType> {
    const result: Array<TryOptionType> = [];

    if (isPackageSolutionListing(listing)) {
        result.push(TryOptionTypes.Sandbox);
    }
    if (isTestDriveListing(listing)) {
        result.push(TryOptionTypes.TestDrive);
    }
    if (isTrialForceListing(listing)) {
        result.push(TryOptionTypes.Trialforce);
    }

    return result;
}

export function isTestDriveListing(listing: ListingResponse) {
    return forceTestDrive(listing)?.enableTestDrive || forceAPITestDrive(listing)?.enableTestDrive;
}

function forceTestDrive(listing: ListingResponse): Force_PackageTestDrive {
    return forcePackageListingExtensions(listing)?.extensions?.find(
        (extension) => extension.extensionType === EXTENSION_TYPE_TEST_DRIVE,
    )?.data as Force_PackageTestDrive;
}

function forceAPITestDrive(listing: ListingResponse): Force_APITestDrive {
    return forceAPIListingExtensions(listing)?.extensions?.find(
        (extension) => extension.extensionType === EXTENSION_TYPE_API,
    )?.data as Force_APITestDrive;
}

export function forcePackageListingExtensions(listing: ListingResponse): Force_PackageListing | undefined {
    const extensionListing = selectExtensionListing(listing);
    return extensionListing?.extensions?.find(
        (extension: Force_Extension) => extension.extensionType === EXTENSION_TYPE_PACKAGE_LISTING,
    )?.data as Force_PackageListing;
}

export function forceAPIListingExtensions(listing: ListingResponse): Force_APIListing | undefined {
    const extensionListing = selectExtensionListing(listing);
    return extensionListing?.extensions?.find(
        (extension: Force_Extension) => extension.extensionType === EXTENSION_TYPE_API_LISTING,
    )?.data as Force_APIListing;
}

export function isPackageSolutionListing(listing: ListingResponse): boolean {
    return forcePackageListingExtensions(listing)?.provisionFlow === 'Package';
}

export function isTrialForceListing(listing: { plugins: { pluginType: string }[] }): boolean {
    return (
        listing?.plugins?.some(
            (plugin: { pluginType: string }) => plugin.pluginType === 'listing/plugins/TrialTemplate',
        ) || false
    );
}

export function isCopilotListing(listing?: ListingResponse) {
    return listing?.technology === 'force/copilot';
}

export function hasAgenticDetails(listing: ListingResponse): boolean {
    if (!isCopilotListing(listing)) {
        return false;
    }
    const extensionListing = selectExtensionListing<schema.oaf.Force_Copilot_Listing>(listing);
    if (!extensionListing) {
        return false;
    }
    return Boolean(extensionListing.numAgents || extensionListing.numTopics || extensionListing.numActions);
}
